import React from 'react';

import { Alert, Badge, Col, Modal, Row, Spinner, Table } from 'react-bootstrap';

import { Food } from 'services/foodService';

interface FoodDetailsModalProps {
  show: boolean;
  food: Food | null;
  loading?: boolean;
  onHide: () => void;
}

const formatNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return Number(value).toFixed(2);
};

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString();
};

const formatNutritionKey = (key: string) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const getAdditionalNutrition = (food: Food): Record<string, unknown> => {
  const additional = food.nutrition?.additional;

  if (!additional) {
    return {};
  }

  if (typeof additional === 'object') {
    return additional;
  }

  try {
    const parsed = JSON.parse(additional);

    if (parsed && typeof parsed === 'object') {
      return parsed;
    }

    return {};
  } catch {
    return {};
  }
};

const formatAdditionalNutritionValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return String(value);
  }

  if (typeof value === 'object') {
    const nutrient = value as {
      amount?: number | string | null;
      unit?: string | null;
      value?: number | string | null;
    };

    const amount = nutrient.amount ?? nutrient.value;

    if (amount !== null && amount !== undefined && amount !== '') {
      const formattedAmount =
        typeof amount === 'number' ? formatNumber(amount) : String(amount);

      const unit = nutrient.unit ? ` ${nutrient.unit}` : '';
      return `${formattedAmount}${unit}`.trim();
    }

    try {
      return JSON.stringify(value);
    } catch {
      return '-';
    }
  }

  return String(value);
};

interface InfoFieldProps {
  label: string;
  value?: React.ReactNode;
  direction?: 'ltr' | 'rtl';
}

const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  direction = 'ltr'
}) => (
  <div className="py-2">
    <div className="text-body-secondary fs-9 mb-1">{label}</div>

    <div className="fw-semibold text-body-emphasis text-break" dir={direction}>
      {value || '-'}
    </div>
  </div>
);

interface NutritionItemProps {
  label: string;
  value: number | string | null | undefined;
  unit: string;
}

const NutritionItem: React.FC<NutritionItemProps> = ({
  label,
  value,
  unit
}) => (
  <div className="border rounded-2 px-3 py-2 h-100">
    <div className="text-body-secondary fs-9">{label}</div>

    <div className="fw-semibold mt-1">
      {formatNumber(value)}
      <span className="text-body-secondary fs-9 ms-1">{unit}</span>
    </div>
  </div>
);

const SectionTitle: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div className="d-flex align-items-center mb-2 mt-3">
    <h6 className="mb-0 fw-bold">{children}</h6>
  </div>
);

const FoodDetailsModal: React.FC<FoodDetailsModalProps> = ({
  show,
  food,
  loading = false,
  onHide
}) => {
  const additionalNutrition = food ? getAdditionalNutrition(food) : {};

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      contentClassName="border-0"
    >
      {/* Header */}
      <Modal.Header closeButton className="px-4 py-3">
        <div className="d-flex align-items-center gap-3">
          <div>
            <Modal.Title className="fs-6 mb-1">
              {food?.name_en || 'Food Details'}
            </Modal.Title>

            {food?.brand?.name_en && (
              <div className="text-body-secondary fs-9">
                {food.brand.name_en}
              </div>
            )}
          </div>
        </div>
      </Modal.Header>

      {/* Body */}
      <Modal.Body className="px-4 py-2">
        {loading ? (
          <div className="text-center py-6">
            <Spinner animation="border" size="sm" />

            <div className="text-body-secondary fs-9 mt-2">Loading...</div>
          </div>
        ) : !food ? (
          <Alert variant="warning" className="my-3">
            Food details could not be loaded.
          </Alert>
        ) : (
          <>
            {/* ========================= */}
            {/* BASIC INFORMATION */}
            {/* ========================= */}

            <SectionTitle>Basic Information</SectionTitle>

            {/* <div className="border rounded-2 px-3">
              <Row className="gx-4">

                <Col md={3}>
                  <InfoField
                    label="Food ID"
                    value={food.id}
                  />
                </Col>

                    <Col md={3}>
                      <InfoField
                        label="Brand"
                        value={
                          <>
                            <div dir="ltr">
                              {food.brand?.name_en || '-'}
                            </div>

                            <div
                              dir="rtl"
                              className="text-body-secondary mt-1"
                            >
                              {food.brand?.name_ar || '-'}
                            </div>
                          </>
                        }
                      />
                    </Col>

                <Col md={3}>
                  <InfoField
                    label="Category"
                    value={
                      food.category?.name_en
                    }
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Barcode"
                    value={food.barcode}
                  />
                </Col>

                <Col md={6}>
                  <InfoField
                    label="English Name"
                    value={food.name_en}
                  />
                </Col>

                <Col md={6}>
                  <InfoField
                    label="Arabic Name"
                    value={
                      food.name_ar
                    }
                    direction="rtl"
                  />
                </Col>

                {food.description && (
                  <Col xs={12}>
                    <InfoField
                      label="Description"
                      value={
                        food.description
                      }
                    />
                  </Col>
                )}

              </Row>
            </div> */}

            <div className="border rounded-2 px-3">
              <Row className="gx-4">
                {/* Food ID */}
                <Col md={3}>
                  <InfoField label="Food ID" value={food.id} />
                </Col>

                {/* Brand */}
                <Col md={3}>
                  <InfoField
                    label="Brand"
                    value={
                      <div>
                        <div className="fw-semibold">
                          {food.brand?.name_en || '-'}
                        </div>

                        <div
                          dir="rtl"
                          className="text-body-secondary mt-1"
                          style={{ textAlign: 'left' }}
                        >
                          {food.brand?.name_ar || '-'}
                        </div>
                      </div>
                    }
                  />
                </Col>

                {/* Category */}
                <Col md={3}>
                  <InfoField
                    label="Category"
                    value={
                      <div>
                        <div className="fw-semibold">
                          {food.category?.name_en || '-'}
                        </div>

                        <div
                          dir="rtl"
                          className="text-body-secondary mt-1"
                          style={{ textAlign: 'left' }}
                        >
                          {food.category?.name_ar || '-'}
                        </div>
                      </div>
                    }
                  />
                </Col>

                {/* Barcode */}
                <Col md={3}>
                  <InfoField label="Barcode" value={food.barcode} />
                </Col>

                {/* Food Name */}
                <Col xs={12}>
                  <InfoField
                    label="Food Name"
                    value={
                      <div>
                        <div className="fw-semibold">{food.name_en || '-'}</div>

                        <div
                          dir="rtl"
                          className="text-body-secondary mt-1"
                          style={{ textAlign: 'left' }}
                        >
                          {food.name_ar || '-'}
                        </div>
                      </div>
                    }
                  />
                </Col>

                {/* Description */}
                {food.description && (
                  <Col xs={12}>
                    <InfoField label="Description" value={food.description} />
                  </Col>
                )}
              </Row>
            </div>

            {/* ========================= */}
            {/* NUTRITION */}
            {/* ========================= */}

            <SectionTitle>Nutrition (Nutrition values per 100 g)</SectionTitle>

            <Row className="g-2">
              <Col xs={6} md={3}>
                <NutritionItem
                  label="Calories (kcal)"
                  value={food.nutrition?.calories}
                  unit="kcal"
                />
              </Col>

              <Col xs={6} md={3}>
                <NutritionItem
                  label="Protein (g)"
                  value={food.nutrition?.protein_g}
                  unit="g"
                />
              </Col>

              <Col xs={6} md={3}>
                <NutritionItem
                  label="Fat (g)"
                  value={food.nutrition?.fat_g}
                  unit="g"
                />
              </Col>

              <Col xs={6} md={3}>
                <NutritionItem
                  label="Carbohydrates (g)"
                  value={food.nutrition?.carbohydrates_g}
                  unit="g"
                />
              </Col>

              <Col xs={6} md={3}>
                <NutritionItem
                  label="Fiber"
                  value={food.nutrition?.fiber_g}
                  unit="g"
                />
              </Col>

              <Col xs={6} md={3}>
                <NutritionItem
                  label="Sugar"
                  value={food.nutrition?.sugar_g}
                  unit="g"
                />
              </Col>

              <Col xs={6} md={3}>
                <NutritionItem
                  label="Sodium"
                  value={food.nutrition?.sodium_mg}
                  unit="mg"
                />
              </Col>
            </Row>

            {/* ========================= */}
            {/* ADDITIONAL NUTRITION */}
            {/* ========================= */}

            {Object.keys(additionalNutrition).length > 0 && (
              <>
                <SectionTitle>
                  Additional Nutrition (Nutrition values per 100 g)
                </SectionTitle>

                <div className="border rounded-2 overflow-hidden p-2">
                  <Table hover responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Nutrient</th>

                        <th>Value</th>

                        <th>Nutrient</th>

                        <th>Value</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(() => {
                        const entries = Object.entries(additionalNutrition);

                        const rows = [];

                        for (let i = 0; i < entries.length; i += 2) {
                          const first = entries[i];

                          const second = entries[i + 1];

                          rows.push(
                            <tr key={first[0]}>
                              <td>{formatNutritionKey(first[0])}</td>

                              <td>
                                {formatAdditionalNutritionValue(first[1])}
                              </td>

                              <td>
                                {second ? formatNutritionKey(second[0]) : '-'}
                              </td>

                              <td>
                                {second
                                  ? formatAdditionalNutritionValue(second[1])
                                  : '-'}
                              </td>
                            </tr>
                          );
                        }

                        return rows;
                      })()}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {/* ========================= */}
            {/* MEASURES + SYNONYMS */}
            {/* ========================= */}

            <Row className="gx-4">
              {/* Measures */}
              <Col md={6}>
                <SectionTitle>Measures</SectionTitle>

                <div className="border rounded-2 overflow-hidden p-2">
                  {food.measures && food.measures.length > 0 ? (
                    <Table hover responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Measure</th>
                          <th>Quantity</th>
                          <th>Gram Weight</th>
                          <th>Default</th>
                        </tr>
                      </thead>

                      <tbody>
                        {food.measures.map(measure => (
                          <tr key={measure.id}>
                            <td>{measure.measure_name}</td>

                            <td>
                              {measure.quantity !== null &&
                              measure.quantity !== undefined &&
                              measure.quantity !== ''
                                ? `${measure.quantity} ${
                                    measure.unit || ''
                                  }`.trim()
                                : '-'}
                            </td>

                            <td>
                              {measure.gram_weight !== null &&
                              measure.gram_weight !== undefined &&
                              measure.gram_weight !== ''
                                ? `${measure.gram_weight} g`
                                : '-'}
                            </td>

                            <td>
                              {measure.is_default ? (
                                <Badge bg="success" className="fs-10">
                                  Default
                                </Badge>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-body-secondary fs-9 p-3">
                      No measures available.
                    </div>
                  )}
                </div>
              </Col>

              {/* Synonyms */}
              <Col md={6}>
                <SectionTitle>Synonyms</SectionTitle>

                <div className="border rounded-2 overflow-hidden">
                  {food.synonyms && food.synonyms.length > 0 ? (
                    <Table hover responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Language</th>

                          <th>Synonym</th>
                        </tr>
                      </thead>

                      <tbody>
                        {food.synonyms.map(synonym => (
                          <tr key={synonym.id}>
                            <td>{synonym.language.toUpperCase()}</td>

                            <td dir={synonym.language === 'ar' ? 'rtl' : 'ltr'}>
                              {synonym.synonym}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-body-secondary fs-9 p-3">
                      No synonyms available.
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* ========================= */}
            {/* SYSTEM INFORMATION */}
            {/* ========================= */}

            <SectionTitle>System Information</SectionTitle>

            <div className="border rounded-2 px-3">
              <Row className="gx-4">
                <Col md={3}>
                  <InfoField label="USDA FDC ID" value={food.usda_fdc_id} />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Auto Imported"
                    value={
                      food.is_auto_imported ? (
                        <Badge bg="info" className="fs-10">
                          Yes
                        </Badge>
                      ) : (
                        'No'
                      )
                    }
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Verified"
                    value={
                      food.is_verified ? (
                        <Badge bg="success" className="fs-10">
                          Yes
                        </Badge>
                      ) : (
                        'No'
                      )
                    }
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Status"
                    value={
                      food.is_active ? (
                        <Badge bg="success" className="fs-10">
                          Active
                        </Badge>
                      ) : (
                        <Badge bg="secondary" className="fs-10">
                          Inactive
                        </Badge>
                      )
                    }
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Usage Count"
                    value={food.usage_count ?? 0}
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Last Used"
                    value={formatDate(food.last_used_at)}
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Created"
                    value={formatDate(food.created_at)}
                  />
                </Col>

                <Col md={3}>
                  <InfoField
                    label="Updated"
                    value={formatDate(food.updated_at)}
                  />
                </Col>
              </Row>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="px-4 py-2">
        <button
          type="button"
          className="btn btn-phoenix-secondary btn-sm"
          onClick={onHide}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default FoodDetailsModal;
